// Shared logo utilities that can be used in both React components and server-side code
//
// IMPORTANT: ALL LOGO STYLING MUST BE SELF-CONTAINED IN THIS FILE
// DO NOT add font imports or styling in calling code (layout.tsx, components, etc.)
// This ensures the logo works consistently in both React components and server-side middleware

// Logo mode configuration
type LogoMode = 'metamynd' | 'sureshop';
export const LOGO_MODE = 'sureshop' as LogoMode;

export const LOGO_TEXT = {
  WIDE: LOGO_MODE === 'metamynd' ? 'metamynd' : 'SureShop',
  NARROW: LOGO_MODE === 'metamynd' ? 'm' : 'S',
} as const;

const LOGO_FONT = {
  family: LOGO_MODE === 'metamynd' ? 'Dosis, sans-serif' : 'Raleway, sans-serif',
  weight: LOGO_MODE === 'metamynd' ? 700 : 800,
} as const;

interface LogoConfig {
  text: typeof LOGO_TEXT.WIDE | typeof LOGO_TEXT.NARROW;
  heightPx?: number; // Height in pixels
  className?: string;
}

interface LogoHTMLConfig extends LogoConfig {
  tagName?: string;
}

// For middleware.ts usage only - returns the Google Fonts URL
export const getLogoFontUrl = (): string => {
  if (LOGO_MODE === 'metamynd') {
    return 'https://fonts.googleapis.com/css2?family=Dosis:wght@700&display=swap';
  } else {
    return 'https://fonts.googleapis.com/css2?family=Raleway:wght@800&display=swap';
  }
};

export const getLogoCSS = (): string => {
  if (LOGO_MODE === 'metamynd') {
    return `
      .site-logo {
        font-family: 'Dosis', sans-serif;
        font-weight: 700;
        line-height: 1.2;
        background: linear-gradient(135deg, #A32563 0%, #732553 35%, #FF5C8D 50%, #3E4492 75%, #3E4492 100%);
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
        color: transparent;
        display: inline-block;
        text-decoration: none;
        margin: 0;
        padding-bottom: 0.1em;
      }
    `.trim();
  } else {
    return `
      .site-logo {
        font-family: 'Raleway', sans-serif;
        font-weight: 800;
        line-height: 1.2;
        background: linear-gradient(135deg, #A32563 0%, #732553 35%, #FF5C8D 50%, #3E4492 75%, #3E4492 100%);
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
        color: transparent;
        display: inline-block;
        text-decoration: none;
        margin: 0;
        padding-bottom: 0.1em;
      }
    `.trim();
  }
};

export const getLogoHTML = (config: LogoHTMLConfig): string => {
  const { text, heightPx = 32, className = '', tagName = 'span' } = config;
  const style = `font-size: ${heightPx}px;`;

  return `<${tagName} class="site-logo ${className}" style="${style}">${text}</${tagName}>`;
};
